import React from 'react';
import { Button, colors, H1, H3 } from '@tviklax/ui-components';
import { gql } from '@apollo/client';
import { useGraphQLQuery } from '@tviklax/ui-components';
import {
  AttentionItem,
  ShipmentFunnnelStep,
  PerfMetric,
  RouteInsight,
  RiskSignal,
  OrderExceptionEntry,
} from './types';
import {
  DashboardContainer,
  DashboardHeader,
  HeaderContent,
  Subtitle,
  LastUpdated,
  StatsGrid,
  StatCard,
  StatContent,
  StatDetails,
  StatTitle,
  StatValue,
  StatChange,
  StatIcon,
  OrdersCard,
  OrdersHeader,
  TableContainer,
  Table,
  TableHead,
  TableHeader,
  TableBody,
  TableRow,
  TableCell,
  OrderId,
  StatusContainer,
  Amount,
  DashboardGrid,
  FullWidthSection,
  SecondarySection
} from './styles';

import { AttentionRequiredStrip } from './AttentionRequiredStrip';
import { ShipmentFunnel } from './ShipmentFunnel';
import { SLAMetrics } from './SLAMetrics';
import { CostMarginCard } from './CostMarginCard';
import { RouteInsights } from './RouteInsights';
import { RiskSignals } from './RiskSignals';
import { SmartInsights } from './SmartInsights';
import { FinanceSummary } from './FinanceSummary';
import { Icon } from '@tviklax/ui-components';
import { useRole } from '../../../context/RoleContext';
import { BuyerDashboard } from './BuyerDashboard';
import { CustomerDashboard } from './CustomerDashboard';
import { OpsDashboard } from './OpsDashboard';
import { FinanceDashboard } from './FinanceDashboard';
import { ComplianceDashboard } from './ComplianceDashboard';
import { SalesDashboard } from './SalesDashboard';
import { AgentDashboard } from './AgentDashboard';
import { getStatusIcon } from './statusIcon';

interface DashboardProps {
  onViewOperationsHub?: () => void;
  onOrderClick?: (orderId: string) => void;
  onCreateOrder?: () => void;
  onBrowseMarketplace?: () => void;
  onOpenPayments?: (orderId: string) => void;
  onOpenDocuments?: (orderId: string, documentId?: string) => void;
  onOpenMessaging?: (payload: {
    threadId?: number;
    subject?: string;
    orderId?: string;
    targetContactId?: number;
    targetContactName?: string | null;
    targetContactEmail?: string | null;
    draftText?: string;
  }) => void;
  onTrackShipment?: (payload: { orderId: string; shipmentNo?: string | null }) => void;
}

export const Dashboard: React.FC<DashboardProps> = (props) => {
  const { role, can } = useRole();

  if (role === 'customer') {
    return (
      <CustomerDashboard
        onOrderClick={props.onOrderClick}
        onCreateOrder={props.onCreateOrder}
        onBrowseMarketplace={props.onBrowseMarketplace}
        onOpenPayments={props.onOpenPayments}
        onOpenDocuments={props.onOpenDocuments}
        onOpenMessaging={props.onOpenMessaging}
        onTrackShipment={props.onTrackShipment}
      />
    );
  }

  if (can('dashboard.viewLimited')) {
    return (
      <BuyerDashboard
        onOrderClick={props.onOrderClick}
        onCreateOrder={props.onCreateOrder}
        onBrowseMarketplace={props.onBrowseMarketplace}
      />
    );
  }

  switch (role) {
    case 'ops':
      return <OpsDashboard onViewOperationsHub={props.onViewOperationsHub} onOrderClick={props.onOrderClick} />;
    case 'finance':
      return <FinanceDashboard />;
    case 'compliance':
      return <ComplianceDashboard />;
    case 'sales':
      return <SalesDashboard />;
    case 'agent':
      return <AgentDashboard />;
    default:
      return <FullOpsDashboard {...props} />;
  }
};

const FullOpsDashboard: React.FC<DashboardProps> = ({ onViewOperationsHub, onOrderClick }) => {
  const DASHBOARD_METRICS_QUERY = gql`
    query DashboardMetrics {
      dashboardMetrics {
        totalShipments
        activeShipments
        draftOrders
        marginImpact
      }
      slaMetrics {
        label
        value
        trend
        trendValue
      }
      attentionItems {
        label
        count
        priority
      }
      shipmentFunnel {
        stage
        count
        avgTime
        slaWarning
      }
      costProfitSnapshot {
        revenue
        shippingCost
        avgCostPerShipment
        grossMarginPercent
        receivables
        overdue
        nextPayout
        balance
      }
      routeInsights {
        route
        deliveryTime
        cost
        delayRisk
        trend
      }
      riskSignals {
        label
        status
      }
      recentExceptions {
        source
        count
        entries {
          orderId
          shipmentNo
          customerName
          status
          exceptionReason
          amount
        }
      }
    }
  `;

  const { data } = useGraphQLQuery<{
    dashboardMetrics: { totalShipments: number; activeShipments: number; draftOrders: number; marginImpact: number };
    slaMetrics?: Array<{ label: string; value: string; trend?: 'up' | 'down'; trendValue: string }>;
    attentionItems: Array<{ label: string; count: number; priority: string }>;
    shipmentFunnel: Array<{ stage: string; count: number; avgTime?: string; slaWarning?: string }>;
    costProfitSnapshot?: {
      revenue?: number | string;
      shippingCost?: number | string;
      avgCostPerShipment?: number | string;
      grossMarginPercent?: number | string;
      receivables?: number | string;
      overdue?: number | string;
      nextPayout?: number | string;
      balance?: number | string;
    };
    routeInsights?: Array<{ route: string; deliveryTime: string; cost: string; delayRisk: RouteInsight['delayRisk']; trend: RouteInsight['trend'] }>;
    riskSignals?: Array<{ label: string; status: RiskSignal['status'] }>;
    recentExceptions?: { source: string; count: number; entries: OrderExceptionEntry[] };
  }>(DASHBOARD_METRICS_QUERY, {});

  const metrics = data?.dashboardMetrics;
  const attentionItemsLive: AttentionItem[] = data?.attentionItems?.map(a => ({ label: a.label, count: a.count, priority: a.priority as any })) ?? [];
  const funnelStepsLive: ShipmentFunnnelStep[] = data?.shipmentFunnel?.map(s => ({ stage: s.stage, count: s.count, avgTime: s.avgTime ?? undefined, slaWarning: s.slaWarning ?? undefined })) ?? [];
  const slaMetricsLive: PerfMetric[] = data?.slaMetrics?.map((m) => ({ label: m.label, value: m.value, trend: m.trend || 'up', trendValue: m.trendValue })) ?? [];
  const routeInsightsLive: RouteInsight[] = data?.routeInsights?.map((r) => ({ route: r.route, deliveryTime: r.deliveryTime, cost: r.cost, delayRisk: r.delayRisk, trend: r.trend })) ?? [];
  const riskSignalsLive: RiskSignal[] = data?.riskSignals?.map((s) => ({ label: s.label, status: s.status })) ?? [];
  const costProfitSnapshot = data?.costProfitSnapshot;

  const formatAmount = (value: unknown) => {
    if (value === null || value === undefined || value === '') {
      return '—';
    }

    const numericValue = typeof value === 'number'
      ? value
      : Number(String(value).replace(/[^0-9.-]/g, ''));

    if (!Number.isFinite(numericValue)) {
      return '—';
    }

    return `$${numericValue.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  return (
    <DashboardContainer>
      <DashboardHeader initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <HeaderContent>
          <H1 style={{ margin: '0' }}>The Complete Infrastructure for Modern Trade</H1>
          <Subtitle>Unifying production, compliance, finance, documents, multilingual messaging and end-to-end logistics.</Subtitle>
        </HeaderContent>
        <LastUpdated>
          <Icon name="Clock" size={16} />
          <span>Live updates enabled</span>
        </LastUpdated>
      </DashboardHeader>

      <StatsGrid>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Total Shipments</StatTitle>
              <StatValue>{metrics?.totalShipments ?? '—'}</StatValue>
              <StatChange><Icon name="TrendingUp" /><span>+12%</span></StatChange>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="Package" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.2 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Active Shipments</StatTitle>
              <StatValue>{metrics?.activeShipments ?? '—'}</StatValue>
              <StatChange><Icon name="TrendingUp" /><span>+5%</span></StatChange>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="Truck" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Draft Orders</StatTitle>
              <StatValue>{metrics?.draftOrders ?? '—'}</StatValue>
              <StatChange><Icon name="TrendingDown" /><span>-8%</span></StatChange>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="Pencil" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
        <StatCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.4 }}>
          <StatContent>
            <StatDetails>
              <StatTitle>Margin Impact</StatTitle>
              <StatValue>{Number.isFinite(metrics?.marginImpact) ? `${metrics!.marginImpact}%` : '—'}</StatValue>
              <StatChange><Icon name="TrendingUp" /><span>+2.1%</span></StatChange>
            </StatDetails>
            <StatIcon $color={colors.gray100}><Icon name="TrendingUp" color={colors.primary} /></StatIcon>
          </StatContent>
        </StatCard>
      </StatsGrid>

      <AttentionRequiredStrip items={attentionItemsLive} />

      <DashboardGrid>
        <ShipmentFunnel steps={funnelStepsLive} />
        <SLAMetrics metrics={slaMetricsLive} />

        <FullWidthSection>
          <OrdersCard initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
            <OrdersHeader>
              <H3 style={{ margin: '0' }}>Recent Exceptions / High Priority Shipments</H3>
              <Button variant="link" style={{ padding: 0 }} onClick={onViewOperationsHub}>View Operations Hub</Button>
            </OrdersHeader>
            <TableContainer>
              <Table>
                <TableHead>
                  <tr>
                    <TableHeader>ID</TableHeader>
                    <TableHeader>Customer</TableHeader>
                    <TableHeader>Status</TableHeader>
                    <TableHeader>Exception Reason</TableHeader>
                    <TableHeader>Amount</TableHeader>
                  </tr>
                </TableHead>
                <TableBody>
                  {(data?.recentExceptions?.entries || []).map((row, index) => (
                    <TableRow key={`${(row as any).orderId || (row as any).id}-${index}`} initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.1 * index }}>
                      <TableCell><OrderId style={{ cursor: 'pointer', color: colors.primary }} onClick={() => onOrderClick?.((row as any).orderId ?? (row as any).id)}>{(row as any).orderId ?? (row as any).id ?? '—'}</OrderId></TableCell>
                      <TableCell>{(row as any).customerName ?? (row as any).customer ?? '—'}</TableCell>
                      <TableCell>
                        <StatusContainer>{getStatusIcon((row as any).status ?? 'Pending')}<span>{(row as any).status ?? '—'}</span></StatusContainer>
                      </TableCell>
                      <TableCell>
                        <span style={{ color: colors.error, fontWeight: 600, fontSize: '0.75rem' }}>
                          {(row as any).exceptionReason ?? '—'}
                        </span>
                      </TableCell>
                      <TableCell><Amount>{formatAmount((row as any).amount)}</Amount></TableCell>
                    </TableRow>
                  ))}
                  {(!data?.recentExceptions?.entries || data.recentExceptions.entries.length === 0) && (
                    <TableRow initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }}>
                      <TableCell><OrderId>—</OrderId></TableCell>
                      <TableCell>—</TableCell>
                      <TableCell><StatusContainer><span>No exception records</span></StatusContainer></TableCell>
                      <TableCell><span style={{ color: colors.gray500, fontWeight: 500, fontSize: '0.75rem' }}>No database entries</span></TableCell>
                      <TableCell><Amount>—</Amount></TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          </OrdersCard>
        </FullWidthSection>

        <CostMarginCard
          revenue={costProfitSnapshot ? `$${Number(costProfitSnapshot.revenue).toLocaleString()}` : "—"}
          shippingCost={costProfitSnapshot ? `$${Number(costProfitSnapshot.shippingCost).toLocaleString()}` : "—"}
          margin={costProfitSnapshot ? `${Number(costProfitSnapshot.grossMarginPercent).toFixed(2)}%` : "—"}
          avgCostPerShipment={costProfitSnapshot ? `$${Number(costProfitSnapshot.avgCostPerShipment).toLocaleString()}` : "—"}
        />
        <FinanceSummary
          receivables={costProfitSnapshot ? `$${Number(costProfitSnapshot.receivables).toLocaleString()}` : "—"}
          overdue={costProfitSnapshot ? `$${Number(costProfitSnapshot.overdue).toLocaleString()}` : "—"}
          nextPayout={costProfitSnapshot?.nextPayout ? String(costProfitSnapshot.nextPayout) : '—'}
          balance={costProfitSnapshot ? `$${Number(costProfitSnapshot.balance).toLocaleString()}` : "—"}
        />

        <SecondarySection style={{ gridColumn: '1 / -1' }}>
          <RouteInsights insights={routeInsightsLive} />
          <RiskSignals signals={riskSignalsLive} />
        </SecondarySection>

        <FullWidthSection>
          <SmartInsights insights={[]} />
        </FullWidthSection>
      </DashboardGrid>
    </DashboardContainer>
  );
};
